<template>
  <div
    class="el-select dm-select"
    :class="[selectSize ? 'el-select--' + selectSize : '']"
    @click.stop="toggleMenu"
    v-clickoutside="handleClose"
    @mouseenter="inputHovering = true"
    @mouseleave="inputHovering = false"
  >
    <div
      class="el-select__tags"
      v-if="multiple"
      ref="tags"
      :style="{ 'max-width': inputWidth - 32 + 'px', width: '100%' }"
    >
      <span v-if="collapseTags && selected.length">
        <el-tag
          :closable="!selectDisabled"
          :size="collapseTagSize"
          :hit="selected[0].hitState"
          type="info"
          @close="deleteTag($event, selected[0])"
          disable-transitions
        >
          <span class="el-select__tags-text">{{ selected[0].currentLabel }}</span>
        </el-tag>
        <el-tag
          v-if="selected.length > 1"
          :closable="false"
          :size="collapseTagSize"
          type="info"
          disable-transitions
        >
          <span class="el-select__tags-text">+ {{ selected.length - 1 }}</span>
        </el-tag>
      </span>
      <transition-group @after-leave="resetInputHeight" v-if="!collapseTags">
        <div v-for="item in selected" :key="getValueKey(item)">
          <slot name="tagOut" :option="item">
            <el-tag
              :closable="!selectDisabled"
              :size="collapseTagSize"
              :hit="item.hitState"
              type="info"
              @close="deleteTag($event, item)"
              disable-transitions
            >
              <span class="el-select__tags-text">{{ item.currentLabel }}</span>
            </el-tag>
          </slot>
        </div>
      </transition-group>

      <input
        type="text"
        class="el-select__input"
        :class="[selectSize ? `is-${selectSize}` : '']"
        :disabled="selectDisabled"
        :autocomplete="autoComplete || autocomplete"
        @focus="handleFocus"
        @blur="softFocus = false"
        @keyup="managePlaceholder"
        @keydown="resetInputState"
        @keydown.down.prevent="navigateOptions('next')"
        @keydown.up.prevent="navigateOptions('prev')"
        @keydown.enter.prevent="selectOption"
        @keydown.esc.stop.prevent="visible = false"
        @keydown.delete="deletePrevTag"
        @keydown.tab="visible = false"
        @compositionstart="handleComposition"
        @compositionupdate="handleComposition"
        @compositionend="handleComposition"
        v-model="query"
        @input="debouncedQueryChange"
        v-if="filterable"
        :style="{
          'flex-grow': '1',
          width: inputLength / (inputWidth - 32) + '%',
          'max-width': inputWidth - 42 + 'px'
        }"
        ref="input"
      />
    </div>
    <div
      class="el-select__tags single-el-select__tags"
      v-else-if="selected.currentLabel"
      ref="tags"
      :style="{ 'max-width': inputWidth - 30 + 'px', width: '100%' }"
    >
      <div>
        <slot name="tag" :option="selected">
          <el-tag :size="collapseTagSize" disable-transitions>
            <span class="el-select__tags-text">
              {{ selected.currentLabel[0] }}
            </span>
          </el-tag>
        </slot>
      </div>
    </div>
    <el-input
      ref="reference"
      v-model="selectedLabel"
      type="text"
      :placeholder="currentPlaceholder"
      :name="name"
      :id="id"
      :autocomplete="autoComplete || autocomplete"
      :size="selectSize || size"
      :disabled="selectDisabled"
      :readonly="readonly"
      :validate-event="false"
      :class="{ 'is-focus': visible, 'single-input': !multiple }"
      :tabindex="multiple && filterable ? '-1' : null"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.native="debouncedOnInputChange"
      @keydown.native.down.stop.prevent="navigateOptions('next')"
      @keydown.native.up.stop.prevent="navigateOptions('prev')"
      @keydown.native.enter.prevent="selectOption"
      @keydown.native.esc.stop.prevent="visible = false"
      @keydown.native.tab="visible = false"
      @paste.native="debouncedOnInputChange"
    >
      <template slot="prefix" v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      <template slot="suffix">
        <i
          v-show="!showClose"
          :class="['el-select__caret', 'el-input__icon', 'el-icon-' + iconClass]"
        ></i>
        <i
          v-if="showClose"
          class="el-select__caret el-input__icon el-icon-circle-close"
          @click="handleClearClick"
        ></i>
      </template>
    </el-input>
    <transition name="el-zoom-in-top" @before-enter="handleMenuEnter" @after-leave="doDestroy">
      <el-select-menu
        ref="popper"
        :append-to-body="popperAppendToBody"
        v-show="visible && emptyText !== false"
      >
        <el-scrollbar
          tag="ul"
          wrap-class="el-select-dropdown__wrap"
          view-class="el-select-dropdown__list"
          ref="scrollbar"
          :class="{ 'is-empty': !allowCreate && query && filteredOptionsCount === 0 }"
          v-show="options.length > 0 && !loading"
        >
          <el-option :value="query" created v-if="showNewOption" style="color:#4598fb">
            {{ "添加新选项：" + query }}
          </el-option>
          <slot></slot>
        </el-scrollbar>
        <template
          v-if="emptyText && (!allowCreate || loading || (allowCreate && options.length === 0))"
        >
          <slot name="empty" v-if="$slots.empty"></slot>
          <p class="el-select-dropdown__empty" v-else>
            {{ emptyText }}
          </p>
        </template>
      </el-select-menu>
    </transition>
  </div>
</template>

<script>
import { Select } from "element-ui"

export default {
  name: "DmSelect",
  extends: Select,
  props: {
    size: String
  },
  data() {
    return {
      showClose_new: false
    }
  }
}
</script>

<style scoped lang="scss">
.el-select {
  min-height: 32px;
  .single-el-select__tags {
    min-height: 32px;
    position: relative;
    top: unset;
    transform: unset;
    ::v-deep {
      .single-tag {
        height: unset;
        white-space: normal;
        display: inline-flex !important;
      }
    }
  }
  .single-input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    ::v-deep {
      .el-input__inner {
        height: 100% !important;
      }
    }
  }
}
.el-select-dropdown {
  max-width: 320px;
  ::v-deep {
    .el-select-dropdown__item {
      height: unset;
    }
    .custom-option {
      white-space: normal;
      line-height: 17px;
      display: inline-flex;
      word-break: break-all;
    }
  }
}
</style>
